import { Button } from '@akin-home/buttons/native';
import { ChatResponseOption } from '@akin-home/chat';
import { Text } from '@akin-home/text/native';
import { spacing } from '@akin-home/theme';
import { useEffect, useState } from 'react';
import {
  ScrollView,
  ScrollViewProps,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import Animated, {
  FadeIn,
  FadeInLeft,
  FadeOut,
  FadeOutRight,
} from 'react-native-reanimated';
import styled from 'styled-components/native';
import FullScreenLoader from '../../components/FullScreenLoader';
import PegasusAvatar from '../../components/Pegasus/PegasusAvatar';
import { startVoiceListening } from '../../speech-to-text/useSTT';
import { useStateStore } from '../../stores/useStateStore';
import { useLoginScreenState } from './login_screen_state';

type Props = {
  continue(): void;
};

export function WelcomeScreen(props: Props) {
  const [loading, setLoading] = useState(true);

  const sendLabel = useLoginScreenState((state) => state.sendLabel);
  const sendMessage = useLoginScreenState((state) => state.sendMessage);
  const chat_item = useLoginScreenState((state) => state.chat_item);
  const error_in_welcome = useLoginScreenState(
    (state) => state.error_in_welcome
  );
  const closeWelcome = useLoginScreenState((state) => state.closeWelcome);

  const listening = useStateStore((state) => state.listening_to_voice);

  function onPressOption(option: ChatResponseOption) {
    if (option.input.value === 'start_listening') {
      startVoiceListening();
      return;
    }

    if (option.input.type === 'labeled_intent') {
      sendLabel(option.input.value);
    } else {
      sendMessage(option.input.value);
    }
  }

  useEffect(() => {
    sendLabel('Onboarding_pre_login').then(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { width, height } = useWindowDimensions();
  const isPortrait = width < height;
  const isSmall = (isPortrait && width < 500) || (!isPortrait && height < 500);
  const styles = isPortrait ? portraitStyles : landscapeStyles;

  if (loading) {
    return <FullScreenLoader />;
  }

  return (
    <Container
      key="welcome-screen"
      style={styles.container}
      entering={FadeIn.duration(600)}
      exiting={FadeOut.duration(800)}
    >
      <AvatarContainer
        style={styles.avatarContainer}
        onPress={startVoiceListening}
        onLongPress={closeWelcome}
      >
        <PegasusAvatar expression={listening ? 'surprised' : 'neutral'} />
      </AvatarContainer>

      <TextContainer
        style={styles.textContainer}
        contentContainerStyle={styles.textContentContainer}
      >
        <TextContent
          key={chat_item?.data.text}
          entering={FadeInLeft.duration(800).delay(200)}
          exiting={FadeOutRight.duration(800)}
        >
          <Content variant={isSmall ? 'h2' : 'h1'}>
            {chat_item?.data.text}
          </Content>

          {chat_item?.data.options?.map((option, idx) => (
            <ContinueButton
              key={option.input.value + idx}
              onPress={() => onPressOption(option)}
              size={isSmall ? 'large' : 'xlarge'}
            >
              {option.label}
            </ContinueButton>
          ))}
        </TextContent>

        {error_in_welcome ? (
          <ContinueButton
            onPress={props.continue}
            size={isSmall ? 'large' : 'xlarge'}
          >
            Continue
          </ContinueButton>
        ) : null}
      </TextContainer>
    </Container>
  );
}

const Container = styled(Animated.View)`
  flex: 1;

  background-color: white;
  align-items: center;
`;

const Content = styled(Text)`
  text-align: center;
`;

const TextContainer = styled(ScrollView).attrs<
  unknown,
  Partial<ScrollViewProps>
>((props) => ({
  ...props,
  contentContainerStyle: StyleSheet.compose(
    {
      padding: spacing[3],
      flexGrow: 1,
      width: '100%',
      maxWidth: 600,
      justifyContent: 'center',
      alignSelf: 'center',
      alignItems: 'center',
    },
    props.contentContainerStyle
  ),
}))`
  flex: 3;
  gap: ${spacing[4]}px;
`;

const TextContent = styled(Animated.View)`
  gap: 10px;
`;

const AvatarContainer = styled.TouchableOpacity`
  position: relative;
  flex: 2;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

const ContinueButton = styled(Button)`
  min-width: 60%;
  max-width: 100%;
`;

const portraitStyles = StyleSheet.create({
  container: {
    flexDirection: 'column',
  },
  avatarContainer: {
    width: '100%',
  },
  textContainer: {
    width: '100%',
  },
  textContentContainer: {},
});

const landscapeStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
  },
  avatarContainer: {
    height: '100%',
  },
  textContainer: {
    height: '100%',
  },
  textContentContainer: {
    minHeight: '100%',
  },
});
